import React, { useState } from 'react';
import { Edit2, Save, X, Plus, Trash2, User, MapPin, Briefcase, Mail, MessageSquare, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { SectionCollapsible } from '@/components/directories/profile/SectionCollapsible';

interface Address {
	id: string;
	type: string;
	street: string;
	city: string;
	state: string;
	zipCode: string;
	country: string;
}

interface Relation {
	id: string;
	name: string;
	relationshipType: string;
	graduationYear: string;
}

interface JobOccupation {
	id: string;
	company: string;
	position: string;
	startDate: string;
	endDate: string;
	current: boolean;
}

interface Email {
	id: string;
	email: string;
	type: string;
}

interface Testimonial {
	id: string;
	content: string;
	author: string;
	date: string;
}

interface AlumniProfile {
	name: string;
	birthdate: string;
	gender: string;
	religion: string;
	title: string;
	nickname: string;
	addresses: Address[];
	relations: Relation[];
	jobs: JobOccupation[];
	emails: Email[];
	testimonials: Testimonial[];
}

const AlumniProfileView: React.FC = () => {
	const [isEditing, setIsEditing] = useState(false);
	const [expandedSections, setExpandedSections] = useState<{ [key: string]: boolean }>({
		basic: true,
		addresses: true,
		emails: true,
		jobs: true,
		relations: true,
		testimonials: true
	});
	const [profile, setProfile] = useState<AlumniProfile>({
		name: 'John Michael Anderson',
		birthdate: '1995-06-15',
		gender: 'Male',
		religion: 'Christian',
		title: 'Software Engineer',
		nickname: 'Johnny',
		addresses: [
			{ id: '1', type: 'Home', street: '123 Oak Street', city: 'San Francisco', state: 'CA', zipCode: '94102', country: 'USA' },
			{ id: '2', type: 'Work', street: '456 Market St', city: 'San Francisco', state: 'CA', zipCode: '94105', country: 'USA' }
		],
		relations: [
			{ id: '1', name: 'Sarah Anderson', relationshipType: 'Classmate', graduationYear: '2017' },
			{ id: '2', name: 'David Chen', relationshipType: 'Roommate', graduationYear: '2017' }
		],
		jobs: [
			{ id: '1', company: 'Tech Corp', position: 'Senior Engineer', startDate: '2020-01', endDate: '', current: true },
			{ id: '2', company: 'StartupXYZ', position: 'Junior Developer', startDate: '2017-06', endDate: '2019-12', current: false }
		],
		emails: [
			{ id: '1', email: 'john.anderson@email.com', type: 'Personal' },
			{ id: '2', email: 'j.anderson@techcorp.com', type: 'Work' }
		],
		testimonials: [
			{ id: '1', content: 'John was an exceptional student and always willing to help others.', author: 'Prof. Smith', date: '2023-05-10' },
			{ id: '2', content: 'A great team player and natural leader during our time together.', author: 'Sarah Anderson', date: '2023-06-22' }
		]
	});

	const toggleSection = (section: string) => {
		setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
	};

	const handleSave = () => {
		setIsEditing(false);
		console.log('Saved profile:', profile);
	};

	const updateField = (field: keyof AlumniProfile, value: any) => {
		setProfile({ ...profile, [field]: value });
	};

	const addItem = (field: 'addresses' | 'relations' | 'jobs' | 'emails' | 'testimonials') => {
		const newItem: any = { id: Date.now().toString() };

		if (field === 'addresses') {
			Object.assign(newItem, { type: '', street: '', city: '', state: '', zipCode: '', country: '' });
		} else if (field === 'relations') {
			Object.assign(newItem, { name: '', relationshipType: '', graduationYear: '' });
		} else if (field === 'jobs') {
			Object.assign(newItem, { company: '', position: '', startDate: '', endDate: '', current: false });
		} else if (field === 'emails') {
			Object.assign(newItem, { email: '', type: '' });
		} else if (field === 'testimonials') {
			Object.assign(newItem, { content: '', author: '', date: new Date().toISOString().split('T')[0] });
		}

		setProfile({ ...profile, [field]: [...profile[field], newItem] });
	};

	const removeItem = (field: 'addresses' | 'relations' | 'jobs' | 'emails' | 'testimonials', id: string) => {
		setProfile({ ...profile, [field]: profile[field].filter((item: any) => item.id !== id) });
	};

	const updateArrayItem = (field: 'addresses' | 'relations' | 'jobs' | 'emails' | 'testimonials', id: string, updates: any) => {
		setProfile({
			...profile,
			[field]: profile[field].map((item: any) => item.id === id ? { ...item, ...updates } : item)
		});
	};

	const SectionHeader = ({ title, icon: Icon, section, count }: { title: string; icon: any; section: string; count?: number }) => (
		<button
			onClick={() => toggleSection(section)}
			className="w-full flex items-center justify-between p-3 bg-linear-to-r from-slate-50 to-slate-100 hover:from-slate-100 hover:to-slate-200 rounded-lg transition-all mb-2"
		>
			<div className="flex items-center gap-2">
				<Icon className="w-5 h-5 text-indigo-600" />
				<h3 className="font-semibold text-gray-800">{title}</h3>
				{count !== undefined && (
					<span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">{count}</span>
				)}
			</div>
			{expandedSections[section] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
		</button>
	);

	return (
		<div className="min-h-screen bp-2 sm:p-4 lg:p-6">
			<div className="max-w-4xl mx-auto">
				{/* Compact Header */}
				<div className="bg-(--color-surface) rounded-md shadow-sm overflow-hidden mb-3">
					<div className="bg-linear-to-r from-indigo-600 via-purple-600 to-pink-600 p-4 sm:p-6">
						<div className="flex items-start justify-between gap-3">
							<div className="flex items-center gap-3 flex-1 min-w-0">
								<div className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full flex items-center justify-center shrink-0 shadow-xs">
									<User className="w-8 h-8 sm:w-10 sm:h-10 text-indigo-600" />
								</div>
								<div className="min-w-0 flex-1">
									{isEditing ? (
										<>
											<input
												type="text"
												value={profile.name}
												onChange={(e) => updateField('name', e.target.value)}
												className="w-full text-xl sm:text-2xl font-bold bg-white/20 px-2 py-1 rounded text-white mb-1"
											/>
											<input
												type="text"
												value={profile.nickname}
												onChange={(e) => updateField('nickname', e.target.value)}
												className="w-full text-sm bg-white/20 px-2 py-0.5 rounded text-white"
												placeholder="Nickname"
											/>
										</>
									) : (
										<>
											<h1 className="text-xl sm:text-2xl font-bold text-white truncate">{profile.name}</h1>
											<p className="text-sm text-white/90">"{profile.nickname}"</p>
										</>
									)}
								</div>
							</div>
							<div className="flex gap-2 shrink-0">
								{!isEditing ? (
									<button
										onClick={() => setIsEditing(true)}
										className="p-2 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 transition shadow-xs"
										title="Edit"
									>
										<Edit2 className="w-4 h-4 sm:w-5 sm:h-5" />
									</button>
								) : (
									<>
										<button
											onClick={handleSave}
											className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition shadow-xs"
											title="Save"
										>
											<Save className="w-4 h-4 sm:w-5 sm:h-5" />
										</button>
										<button
											onClick={() => setIsEditing(false)}
											className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition shadow-xs"
											title="Cancel"
										>
											<X className="w-4 h-4 sm:w-5 sm:h-5" />
										</button>
									</>
								)}
							</div>
						</div>
					</div>

					{/* Basic Info - Always visible, compact */}
					<div className="p-4">
						<SectionHeader title="Basic Information" icon={User} section="basic" />
						{expandedSections.basic && (
							<div className="grid grid-cols-2 gap-2 sm:gap-3 mb-2">
								<div>
									<label className="text-xs font-medium text-gray-600">Title</label>
									{isEditing ? (
										<input type="text" value={profile.title} onChange={(e) => updateField('title', e.target.value)} className="w-full px-2 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-indigo-500" />
									) : (
										<p className="text-sm font-medium text-gray-900">{profile.title}</p>
									)}
								</div>
								<div>
									<label className="text-xs font-medium text-gray-600">Birth Date</label>
									{isEditing ? (
										<input type="date" value={profile.birthdate} onChange={(e) => updateField('birthdate', e.target.value)} className="w-full px-2 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-indigo-500" />
									) : (
										<p className="text-sm text-gray-900">{new Date(profile.birthdate).toLocaleDateString()}</p>
									)}
								</div>
								<div>
									<label className="text-xs font-medium text-gray-600">Gender</label>
									{isEditing ? (
										<select value={profile.gender} onChange={(e) => updateField('gender', e.target.value)} className="w-full px-2 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-indigo-500">
											<option>Male</option>
											<option>Female</option>
											<option>Other</option>
										</select>
									) : (
										<p className="text-sm text-gray-900">{profile.gender}</p>
									)}
								</div>
								<div>
									<label className="text-xs font-medium text-gray-600">Religion</label>
									{isEditing ? (
										<input type="text" value={profile.religion} onChange={(e) => updateField('religion', e.target.value)} className="w-full px-2 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-indigo-500" />
									) : (
										<p className="text-sm text-gray-900">{profile.religion}</p>
									)}
								</div>
							</div>
						)}
					</div>
				</div>

				{/* Collapsible Sections */}
				<div className="space-y-3">
					{/* Emails - Compact */}
					<div className="bg-(--color-surface) rounded-md shadow-sm p-4">
						<SectionCollapsible Icon={Mail} title="Emails" count={profile.emails.length} isEditing={isEditing} onAdd={() => addItem('emails')}>
							<div className="space-y-2">
								{profile.emails.map((email) => (
									<div key={email.id} className="flex items-center gap-2 p-2 bg-linear-to-r from-indigo-50 to-purple-50 rounded-lg">
										<div className="flex-1 min-w-0">
											{isEditing ? (
												<div className="flex flex-col sm:flex-row gap-1.5">
													<input type="email" value={email.email} onChange={(e) => updateArrayItem('emails', email.id, { email: e.target.value })} className="flex-1 px-2 py-1 text-sm border rounded" placeholder="Email" />
													<input type="text" value={email.type} onChange={(e) => updateArrayItem('emails', email.id, { type: e.target.value })} className="w-full sm:w-24 px-2 py-1 text-sm border rounded" placeholder="Type" />
												</div>
											) : (
												<div className="flex items-center justify-between gap-2">
													<p className="text-sm text-gray-900 truncate">{email.email}</p>
													<span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full shrink-0">{email.type}</span>
												</div>
											)}
										</div>
										{isEditing && (
											<button onClick={() => removeItem('emails', email.id)} className="p-1 text-red-500 hover:bg-red-50 rounded">
												<Trash2 className="w-4 h-4" />
											</button>
										)}
									</div>
								))}
							</div>
						</SectionCollapsible>
					</div>

					{/* Emails - Compact */}
					<div className="bg-(--color-surface) rounded-md shadow-sm p-4">
						<div className="flex items-center justify-between mb-2">
							<SectionHeader title="Emails" icon={Mail} section="emails" count={profile.emails.length} />
							{isEditing && expandedSections.emails && (
								<button onClick={() => addItem('emails')} className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg">
									<Plus className="w-4 h-4" />
								</button>
							)}
						</div>
						{expandedSections.emails && (
							<div className="space-y-2">
								{profile.emails.map((email) => (
									<div key={email.id} className="flex items-center gap-2 p-2 bg-linear-to-r from-indigo-50 to-purple-50 rounded-lg">
										<div className="flex-1 min-w-0">
											{isEditing ? (
												<div className="flex flex-col sm:flex-row gap-1.5">
													<input type="email" value={email.email} onChange={(e) => updateArrayItem('emails', email.id, { email: e.target.value })} className="flex-1 px-2 py-1 text-sm border rounded" placeholder="Email" />
													<input type="text" value={email.type} onChange={(e) => updateArrayItem('emails', email.id, { type: e.target.value })} className="w-full sm:w-24 px-2 py-1 text-sm border rounded" placeholder="Type" />
												</div>
											) : (
												<div className="flex items-center justify-between gap-2">
													<p className="text-sm text-gray-900 truncate">{email.email}</p>
													<span className="text-xs bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full shrink-0">{email.type}</span>
												</div>
											)}
										</div>
										{isEditing && (
											<button onClick={() => removeItem('emails', email.id)} className="p-1 text-red-500 hover:bg-red-50 rounded">
												<Trash2 className="w-4 h-4" />
											</button>
										)}
									</div>
								))}
							</div>
						)}
					</div>

					{/* Addresses */}
					<div className="bg-(--color-surface) rounded-md shadow-sm p-4">
						<div className="flex items-center justify-between mb-2">
							<SectionHeader title="Addresses" icon={MapPin} section="addresses" count={profile.addresses.length} />
							{isEditing && expandedSections.addresses && (
								<button onClick={() => addItem('addresses')} className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg">
									<Plus className="w-4 h-4" />
								</button>
							)}
						</div>
						{expandedSections.addresses && (
							<div className="space-y-2">
								{profile.addresses.map((addr) => (
									<div key={addr.id} className="p-3 bg-linear-to-br from-indigo-50 to-purple-50 rounded-lg">
										{isEditing && (
											<button onClick={() => removeItem('addresses', addr.id)} className="float-right p-1 text-red-500 hover:bg-red-50 rounded">
												<Trash2 className="w-3.5 h-3.5" />
											</button>
										)}
										{isEditing ? (
											<div className="grid grid-cols-2 gap-2">
												<input type="text" value={addr.type} onChange={(e) => updateArrayItem('addresses', addr.id, { type: e.target.value })} className="col-span-2 px-2 py-1 text-sm border rounded" placeholder="Type" />
												<input type="text" value={addr.street} onChange={(e) => updateArrayItem('addresses', addr.id, { street: e.target.value })} className="col-span-2 px-2 py-1 text-sm border rounded" placeholder="Street" />
												<input type="text" value={addr.city} onChange={(e) => updateArrayItem('addresses', addr.id, { city: e.target.value })} className="px-2 py-1 text-sm border rounded" placeholder="City" />
												<input type="text" value={addr.state} onChange={(e) => updateArrayItem('addresses', addr.id, { state: e.target.value })} className="px-2 py-1 text-sm border rounded" placeholder="State" />
												<input type="text" value={addr.zipCode} onChange={(e) => updateArrayItem('addresses', addr.id, { zipCode: e.target.value })} className="px-2 py-1 text-sm border rounded" placeholder="Zip" />
												<input type="text" value={addr.country} onChange={(e) => updateArrayItem('addresses', addr.id, { country: e.target.value })} className="px-2 py-1 text-sm border rounded" placeholder="Country" />
											</div>
										) : (
											<div>
												<p className="text-xs font-semibold text-indigo-600 mb-1">{addr.type}</p>
												<p className="text-sm text-gray-900">{addr.street}</p>
												<p className="text-xs text-gray-600">{addr.city}, {addr.state} {addr.zipCode}</p>
												<p className="text-xs text-gray-500">{addr.country}</p>
											</div>
										)}
									</div>
								))}
							</div>
						)}
					</div>

					{/* Jobs */}
					<div className="bg-(--color-surface) rounded-md shadow-sm p-4">
						<div className="flex items-center justify-between mb-2">
							<SectionHeader title="Career" icon={Briefcase} section="jobs" count={profile.jobs.length} />
							{isEditing && expandedSections.jobs && (
								<button onClick={() => addItem('jobs')} className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg">
									<Plus className="w-4 h-4" />
								</button>
							)}
						</div>
						{expandedSections.jobs && (
							<div className="space-y-2">
								{profile.jobs.map((job) => (
									<div key={job.id} className="p-3 bg-linear-to-br from-purple-50 to-pink-50 rounded-lg">
										{isEditing && (
											<button onClick={() => removeItem('jobs', job.id)} className="float-right p-1 text-red-500 hover:bg-red-50 rounded">
												<Trash2 className="w-3.5 h-3.5" />
											</button>
										)}
										{isEditing ? (
											<div className="grid grid-cols-2 gap-2">
												<input type="text" value={job.company} onChange={(e) => updateArrayItem('jobs', job.id, { company: e.target.value })} className="col-span-2 px-2 py-1 text-sm border rounded" placeholder="Company" />
												<input type="text" value={job.position} onChange={(e) => updateArrayItem('jobs', job.id, { position: e.target.value })} className="col-span-2 px-2 py-1 text-sm border rounded" placeholder="Position" />
												<input type="month" value={job.startDate} onChange={(e) => updateArrayItem('jobs', job.id, { startDate: e.target.value })} className="px-2 py-1 text-sm border rounded" />
												<input type="month" value={job.endDate} onChange={(e) => updateArrayItem('jobs', job.id, { endDate: e.target.value })} disabled={job.current} className="px-2 py-1 text-sm border rounded disabled:bg-gray-100" />
												<label className="col-span-2 flex items-center text-xs gap-1">
													<input type="checkbox" checked={job.current} onChange={(e) => updateArrayItem('jobs', job.id, { current: e.target.checked, endDate: e.target.checked ? '' : job.endDate })} />
													Currently working here
												</label>
											</div>
										) : (
											<div>
												<p className="font-semibold text-sm text-gray-900">{job.company}</p>
												<p className="text-xs text-gray-700">{job.position}</p>
												<p className="text-xs text-gray-500 mt-1">{job.startDate} - {job.current ? <span className="text-green-600 font-medium">Present</span> : job.endDate}</p>
											</div>
										)}
									</div>
								))}
							</div>
						)}
					</div>

					{/* Relations */}
					<div className="bg-(--color-surface) rounded-md shadow-sm p-4">
						<div className="flex items-center justify-between mb-2">
							<SectionHeader title="Connections" icon={Users} section="relations" count={profile.relations.length} />
							{isEditing && expandedSections.relations && (
								<button onClick={() => addItem('relations')} className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg">
									<Plus className="w-4 h-4" />
								</button>
							)}
						</div>
						{expandedSections.relations && (
							<div className="space-y-2">
								{profile.relations.map((rel) => (
									<div key={rel.id} className="p-2.5 bg-linear-to-r from-purple-50 to-indigo-50 rounded-lg flex items-center gap-2">
										<div className="flex-1 min-w-0">
											{isEditing ? (
												<div className="grid grid-cols-3 gap-1.5">
													<input type="text" value={rel.name} onChange={(e) => updateArrayItem('relations', rel.id, { name: e.target.value })} className="px-2 py-1 text-sm border rounded" placeholder="Name" />
													<input type="text" value={rel.relationshipType} onChange={(e) => updateArrayItem('relations', rel.id, { relationshipType: e.target.value })} className="px-2 py-1 text-sm border rounded" placeholder="Type" />
													<input type="text" value={rel.graduationYear} onChange={(e) => updateArrayItem('relations', rel.id, { graduationYear: e.target.value })} className="px-2 py-1 text-sm border rounded" placeholder="Year" />
												</div>
											) : (
												<div className="flex items-center justify-between gap-2">
													<div className="min-w-0 flex-1">
														<p className="text-sm font-medium text-gray-900 truncate">{rel.name}</p>
														<p className="text-xs text-gray-600">{rel.relationshipType}</p>
													</div>
													<span className="text-xs text-gray-500 shrink-0">'{rel.graduationYear.slice(-2)}</span>
												</div>
											)}
										</div>
										{isEditing && (
											<button onClick={() => removeItem('relations', rel.id)} className="p-1 text-red-500 hover:bg-red-50 rounded">
												<Trash2 className="w-4 h-4" />
											</button>
										)}
									</div>
								))}
							</div>
						)}
					</div>

					{/* Testimonials */}
					<div className="bg-(--color-surface) rounded-md shadow-sm p-4">
						<div className="flex items-center justify-between mb-2">
							<SectionHeader title="Testimonials" icon={MessageSquare} section="testimonials" count={profile.testimonials.length} />
							{isEditing && expandedSections.testimonials && (
								<button onClick={() => addItem('testimonials')} className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg">
									<Plus className="w-4 h-4" />
								</button>
							)}
						</div>
						{expandedSections.testimonials && (
							<div className="space-y-2">
								{profile.testimonials.map((test) => (
									<div key={test.id} className="p-3 bg-linear-to-br from-pink-50 to-purple-50 rounded-lg border-l-4 border-indigo-500">
										{isEditing && (
											<button onClick={() => removeItem('testimonials', test.id)} className="float-right p-1 text-red-500 hover:bg-red-50 rounded">
												<Trash2 className="w-3.5 h-3.5" />
											</button>
										)}
										{isEditing ? (
											<div className="space-y-2">
												<textarea value={test.content} onChange={(e) => updateArrayItem('testimonials', test.id, { content: e.target.value })} className="w-full px-2 py-1.5 text-sm border rounded" rows={2} placeholder="Content" />
												<div className="flex gap-2">
													<input type="text" value={test.author} onChange={(e) => updateArrayItem('testimonials', test.id, { author: e.target.value })} className="flex-1 px-2 py-1 text-sm border rounded" placeholder="Author" />
													<input type="date" value={test.date} onChange={(e) => updateArrayItem('testimonials', test.id, { date: e.target.value })} className="px-2 py-1 text-sm border rounded" />
												</div>
											</div>
										) : (
											<div>
												<p className="text-sm text-gray-700 italic mb-2">"{test.content}"</p>
												<div className="flex justify-between items-center text-xs">
													<span className="font-medium text-gray-900">— {test.author}</span>
													<span className="text-gray-500">{new Date(test.date).toLocaleDateString()}</span>
												</div>
											</div>
										)}
									</div>
								))}
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default AlumniProfileView;